// Scheduling module: public booking links (Calendly-style) + bookings.
// The weekly availability template is expressed in the LINK's timezone;
// slots returned by the public API are UTC ISO strings that the booking page
// renders in the invitee's timezone.

export type SchedulingLocationType = 'VIDEO' | 'PHONE' | 'CUSTOM'

export type SchedulingBookingStatus = 'CONFIRMED' | 'CANCELLED'

export const SCHEDULING_WEEKDAYS = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
] as const

export type SchedulingWeekday = (typeof SCHEDULING_WEEKDAYS)[number]

/** One bookable window inside a day, 24h 'HH:mm' in the link's timezone */
export interface SchedulingAvailabilityInterval {
  start: string
  end: string
}

/** Weekly template: absent day = unavailable */
export type SchedulingAvailabilityTemplate = Partial<
  Record<SchedulingWeekday, SchedulingAvailabilityInterval[]>
>

// ---------------------------------------------------------------------------
// Shared availability schedules (org-level named weekly hours, referenced by
// booking links and live-chat business hours)
// ---------------------------------------------------------------------------

export interface AvailabilityScheduleDTO {
  id: string
  name: string
  /** IANA timezone the weekly template is expressed in */
  timezone: string
  weekly: SchedulingAvailabilityTemplate
  /** At most one per org; new booking links pre-select it */
  isDefault: boolean
  /** Booking links currently referencing this schedule (delete protection) */
  linkCount: number
  /** Whether the org's live-chat business hours reference this schedule */
  usedByLiveChat: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateAvailabilityScheduleDTO {
  name: string
  timezone: string
  weekly: SchedulingAvailabilityTemplate
  isDefault?: boolean
}

export type UpdateAvailabilityScheduleDTO = Partial<CreateAvailabilityScheduleDTO>

export interface SchedulingLinkDTO {
  id: string
  name: string
  slug: string
  description: string | null
  durationMinutes: number
  bufferBeforeMinutes: number
  bufferAfterMinutes: number
  rollingDays: number
  minNoticeMinutes: number
  /** Inline fallback — the schedule's timezone wins when one is referenced */
  timezone: string
  /** Inline fallback — the schedule's weekly wins when one is referenced */
  availability: SchedulingAvailabilityTemplate
  /** Shared org schedule; null = custom (inline) hours */
  availabilityScheduleId: string | null
  /** Resolved for display — name of the referenced schedule */
  availabilityScheduleName: string | null
  locationType: SchedulingLocationType
  locationDetail: string | null
  hostUserIds: string[]
  roundRobin: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateSchedulingLinkDTO {
  name: string
  slug?: string
  description?: string | null
  durationMinutes: number
  bufferBeforeMinutes?: number
  bufferAfterMinutes?: number
  rollingDays?: number
  minNoticeMinutes?: number
  timezone: string
  availability: SchedulingAvailabilityTemplate
  /** Shared org schedule; null/absent = custom (inline) hours */
  availabilityScheduleId?: string | null
  locationType: SchedulingLocationType
  locationDetail?: string | null
  hostUserIds: string[]
  roundRobin?: boolean
  isActive?: boolean
}

export type UpdateSchedulingLinkDTO = Partial<CreateSchedulingLinkDTO>

export interface SchedulingBookingDTO {
  id: string
  linkId: string
  linkName: string
  hostUserId: string
  hostName: string | null
  contactId: string | null
  inviteeName: string
  inviteeEmail: string
  inviteeNotes: string | null
  startTime: string
  endTime: string
  timezone: string
  status: SchedulingBookingStatus
  cancelledAt: string | null
  cancelReason: string | null
  createdAt: string
}

/** Public info shown on the booking page (no host ids leaked) */
export interface PublicBookingLinkInfoDTO {
  organizationName: string
  name: string
  slug: string
  description: string | null
  durationMinutes: number
  timezone: string
  locationType: SchedulingLocationType
  locationDetail: string | null
  rollingDays: number
}

/** One bookable slot, UTC ISO strings */
export interface SchedulingSlotDTO {
  start: string
  end: string
}

export interface PublicCreateBookingDTO {
  /** UTC ISO start of the chosen slot */
  startTime: string
  /** Invitee's IANA timezone */
  timezone: string
  name: string
  email: string
  notes?: string
}

export interface PublicBookingConfirmationDTO {
  id: string
  startTime: string
  endTime: string
  timezone: string
  durationMinutes: number
  linkName: string
  organizationName: string
  locationType: SchedulingLocationType
  locationDetail: string | null
  cancelToken: string
}

/** Data for the public cancellation page */
export interface PublicCancelInfoDTO {
  linkName: string
  organizationName: string
  inviteeName: string
  startTime: string
  endTime: string
  timezone: string
  status: SchedulingBookingStatus
}
